/**
 * Detecta se a mensagem parece conter uma receita
 * @param {string} content - Conteúdo da mensagem
 * @returns {boolean}
 */
export const detectRecipeInMessage = (content) => {
  const recipeKeywords = ['ingredientes:', 'modo de preparo:', 'instruções:', 'porções:', 'tempo de preparo'];
  const hasKeywords = recipeKeywords.some(keyword => 
    content.toLowerCase().includes(keyword)
  );
  return hasKeywords;
};

/**
 * Faz o parsing de uma receita a partir de texto
 * @param {string} content - Conteúdo da mensagem com a receita
 * @returns {object} - Dados estruturados da receita
 */
export const parseRecipeFromMessage = (content) => {
  console.log('Parsing receita:', content);
  
  const lines = content.split('\n').map(line => line.trim()).filter(line => line);
  
  let title = '';
  let description = '';
  let ingredients = [];
  let instructions = [];
  let category = 'Prato Principal';
  let servings = 4;
  let prep_time = 0;
  
  let currentSection = 'title'; // Começa procurando título
  let titleFound = false;
  let descriptionLines = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lowerLine = line.toLowerCase();
    
    // Detectar seções por headers
    if (lowerLine.includes('ingredientes:') || lowerLine.match(/^#+.*ingredientes/i)) {
      currentSection = 'ingredients';
      console.log('Seção ingredientes detectada');
      continue;
    }
    
    if (lowerLine.includes('modo de preparo:') || 
        lowerLine.includes('instruções:') || 
        lowerLine.includes('preparo:') ||
        lowerLine.match(/^#+.*(preparo|instruções)/i)) {
      currentSection = 'instructions';
      console.log('Seção instruções detectada');
      continue;
    }
    
    // Extrair por seção
    if (currentSection === 'title' && !titleFound) {
      // Ignorar linhas de saudação/introdução do assistente
      const skipPatterns = [
        /^(olá|oi|que delícia|vejo|aqui está|claro|perfeito|ótimo)/i,
        /^(recebi|analisando|vou|posso)/i,
        /^[✅🔍❤️👋]/
      ];
      
      const shouldSkip = skipPatterns.some(pattern => pattern.test(line));
      
      // Pegar primeira linha que parece ser um título de receita
      if (line && !line.match(/^[-•\\d.\\*]/) && !shouldSkip) {
        // Se linha está entre ** ou é um heading #, é provavelmente o título
        const cleanedLine = line.replace(/^#+\s*/, '').replace(/\*\*/g, '').trim();
        
        // Verificar se não é muito longo (títulos são geralmente curtos)
        if (cleanedLine.length < 80) {
          title = cleanedLine;
          titleFound = true;
          currentSection = 'description';
          console.log('Título encontrado:', title);
        }
      }
      continue;
    }
    
    if (currentSection === 'description') {
      // Acumular linhas até encontrar uma seção
      if (!lowerLine.includes('ingredientes') && 
          !lowerLine.includes('preparo') &&
          !lowerLine.includes('instruções') &&
          !line.match(/^[-•\\d.]/)) {
        descriptionLines.push(line);
      }
      continue;
    }
    
    if (currentSection === 'ingredients') {
      // Linhas que começam com marcador ou número
      if (line.match(/^[-•\d.]/) || line.match(/^\*\s/)) {
        let cleaned = line.replace(/^[-•\d.*]+\s*/, '').trim();
        
        if (cleaned) {
          console.log('Ingrediente encontrado:', cleaned);
          
          // Padrões: "2 xícaras de farinha" ou "200g de açúcar" ou "1 lata de leite condensado (395g)"
          let quantity = '1';
          let unit = 'unidade';
          let name = cleaned;
          
          // Padrão 1: "1 lata de leite condensado" ou "3 colheres de sopa de chocolate"
          const match1 = cleaned.match(/^(\d+(?:[.,]\d+)?)\s+(colheres?\s+de\s+sopa|colheres?\s+de\s+chá|lata|xícaras?|pitadas?|dentes?|folhas?|ramos?|fatias?|unidades?|kg|g|ml|l)\s+de\s+(.+)/i);
          if (match1) {
            quantity = match1[1];
            unit = match1[2];
            name = match1[3].replace(/\s*\([^)]*\)/, '').trim(); // Remove parenteses como (395g)
          } else {
            // Padrão 2: "200g de açúcar" ou "500ml de leite"
            const match2 = cleaned.match(/^(\d+(?:[.,]\d+)?)\s*(g|kg|ml|l)\s+de\s+(.+)/i);
            if (match2) {
              quantity = match2[1];
              unit = match2[2];
              name = match2[3];
            } else {
              // Padrão 3: "Chocolate granulado para decorar" (sem quantidade específica)
              const match3 = cleaned.match(/^([A-Za-zÀ-ÿ\s]+)(?:\s+(?:para|a)\s+.+)?/i);
              if (match3) {
                quantity = 'a gosto';
                unit = '';
                name = match3[1].trim();
              }
            }
          }
          
          ingredients.push({ 
            name: name.trim(), 
            quantity: quantity.trim(), 
            unit: unit.trim() 
          });
        }
      }
    }
    
    if (currentSection === 'instructions') {
      // Linhas que começam com marcador ou número
      if (line.match(/^[-•\d.]/) || line.match(/^\*\s/)) {
        const cleaned = line.replace(/^[-•\d.*]+\s*/, '').trim();
        if (cleaned) {
          console.log('Instrução encontrada:', cleaned);
          instructions.push(cleaned);
        }
      } else if (line && !line.toLowerCase().includes('instruções') && 
                 !line.toLowerCase().includes('preparo') &&
                 !line.match(/^#+/)) {
        // Linha de continuação
        if (instructions.length > 0) {
          instructions[instructions.length - 1] += ' ' + line;
        }
      }
    }
  }
  
  // Processar descrição
  if (descriptionLines.length > 0) {
    description = descriptionLines.join(' ').trim();
  }
  
  // Fallbacks
  if (!title || title.length > 100) {
    // Se título está muito longo, pegar só primeira linha do conteúdo
    const firstLine = lines[0] || 'Receita do Assistente';
    title = firstLine.substring(0, 60).replace(/^#+\\s*/, '').trim();
  }
  
  if (!description) {
    description = 'Receita gerada pelo assistente culinário de IA';
  }
  
  if (ingredients.length === 0) {
    // Tentar pegar qualquer linha como ingrediente
    console.warn('Nenhum ingrediente estruturado encontrado');
    ingredients.push({ 
      name: 'Ver conversa completa para ingredientes', 
      quantity: '1', 
      unit: 'vez' 
    });
  }
  
  if (instructions.length === 0) {
    console.warn('Nenhuma instrução estruturada encontrada');
    instructions.push('Ver conversa completa para modo de preparo');
  }
  
  const result = {
    title: title.substring(0, 100),
    description: description.substring(0, 200),
    ingredients,
    instructions,
    category,
    servings,
    prep_time
  };
  
  console.log('Resultado do parsing:', result);
  return result;
};
